import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Avatar, Button, Drawer, Empty, Modal, Select, Space, Table, Tag, Tooltip, Typography, message } from "antd";
import {
    RefreshCw,
    MessageSquare,
    AlertTriangle,
    ThumbsUp,
    ThumbsDown,
    Inbox,
    Zap,
    Bot,
    ChevronDown,
    ChevronUp,
    ExternalLink,
    Trash2,
    X,
} from "lucide-react";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import "dayjs/locale/fr";

import { deleteProductFeedback, fetchProductFeedback, updateProductFeedbackStatus } from "../../Services/feedback.service";
import FeedbackExplainPanel from "./FeedbackExplainPanel";
import "./ProductFeedbackDashboard.css";

dayjs.extend(relativeTime);
dayjs.locale("fr");

const { Paragraph, Text, Title } = Typography;

const STATUS_VALUES = ["new", "reviewed", "planned", "fixed", "ignored"];
const ACTIONABLE_STATUSES = new Set(["new", "reviewed", "planned"]);

const emptyStatusTotals = () => STATUS_VALUES.reduce((acc, status) => {
    acc[status] = 0;
    return acc;
}, {});

const isActionableStatus = (status) => ACTIONABLE_STATUSES.has(status || "new");

const STATUS_OPTIONS = STATUS_VALUES.map((value) => ({
    value, label: value,
}));

const SENTIMENT_OPTIONS = ["positive", "neutral", "negative"].map((value) => ({
    value, label: value,
}));

const REASON_OPTIONS = [
    "Pas clair", "Mauvais commentaire", "Info manquante",
    "Trop lent", "Bug d'interface", "Autre",
].map((value) => ({ value, label: value }));

const FEATURE_OPTIONS = [
    "global_review", "analysis_stream", "ai_comment", "comment_details",
    "summary", "contract_profile", "metadata", "export", "error", "other",
].map((value) => ({ value, label: value }));

const STATUS_COLOR = {
    new: "blue", reviewed: "geekblue", planned: "gold", fixed: "green", ignored: "default",
};

const SENTIMENT_COLOR = {
    positive: "#1f9d55", neutral: "#6b7890", negative: "#d4373a",
};

const FEATURE_LABEL = {
    global_review: "Revue globale",
    analysis_stream: "Analyse (stream)",
    ai_comment: "Commentaire IA",
    comment_details: "Mémo détaillé",
    summary: "Synthèse",
    contract_profile: "Profil du contrat",
    metadata: "Métadonnées",
    export: "Export",
    error: "Erreur",
    other: "Autre",
};

const SPARK_DAYS = 14;

const short = (value, max = 92) => {
    const text = String(value || "").replace(/\s+/g, " ").trim();
    if (!text) return "—";
    return text.length > max ? `${text.slice(0, max - 1).trim()}…` : text;
};

const formatRelative = (value) => {
    if (!value) return "—";
    const d = dayjs(value);
    return d.isValid() ? d.fromNow() : "—";
};

const formatAbs = (value) => {
    if (!value) return "—";
    const d = dayjs(value);
    return d.isValid() ? d.format("DD/MM/YYYY HH:mm") : "—";
};

const initialsOf = (value) => {
    const parts = String(value || "").trim().split(/\s+/).filter(Boolean);
    if (!parts.length) return "?";
    return ((parts[0][0] || "") + (parts[1]?.[0] || "")).toUpperCase();
};

const SentimentDot = ({ value }) => {
    if (!value) return <span className="sentiment-dot is-empty">—</span>;
    const color = SENTIMENT_COLOR[value] || "#6b7890";
    const Icon = value === "positive" ? ThumbsUp : value === "negative" ? ThumbsDown : MessageSquare;
    return (
        <Tooltip title={value}>
            <span className="sentiment-dot" style={{ color, borderColor: color, background: `${color}14` }}>
                <Icon size={12} strokeWidth={2.4} />
            </span>
        </Tooltip>
    );
};

const KpiCard = ({ icon: Icon, label, value, hint, accent = "default", children }) => (
    <div className={`kpi-card kpi-card--${accent}`}>
        <div className="kpi-card__head">
            <span className="kpi-card__icon"><Icon size={15} strokeWidth={2.2} /></span>
            <span className="kpi-card__label">{label}</span>
        </div>
        <div className="kpi-card__value">{value}</div>
        {hint && <div className="kpi-card__hint">{hint}</div>}
        {children}
    </div>
);

/**
 * Sparkline 14 derniers jours, calculée sur les feedbacks.
 * SVG inline, aucune dépendance.
 */
const Sparkline = ({ buckets }) => {
    const max = Math.max(1, ...buckets.map((b) => b.count));
    const w = 140, h = 36, gap = 2;
    const barW = (w - gap * (buckets.length - 1)) / buckets.length;
    return (
        <svg viewBox={`0 0 ${w} ${h}`} className="kpi-spark" aria-hidden="true">
            {buckets.map((b, i) => {
                const bh = Math.max(2, Math.round((b.count / max) * (h - 4)));
                const x = i * (barW + gap);
                const y = h - bh;
                return (
                    <rect key={i} x={x} y={y} width={barW} height={bh} rx={1.5} ry={1.5}
                        className={b.count > 0 ? "kpi-spark__bar is-active" : "kpi-spark__bar"} />
                );
            })}
        </svg>
    );
};

/**
 * Calcule les statistiques sur la liste fournie.
 */
const computeStats = (rows) => {
    const total = rows.length;
    const counts = { positive: 0, neutral: 0, negative: 0 };
    const actionableCounts = { positive: 0, neutral: 0, negative: 0 };
    const reasons = new Map();
    const features = new Map();
    const actionableReasons = new Map();
    const actionableFeatures = new Map();
    let newCount = 0, autoCount = 0, fixedCount = 0, ignoredCount = 0;
    let actionableCount = 0, actionableAutoCount = 0;

    // Buckets 14 jours
    const today = dayjs().startOf("day");
    const buckets = Array.from({ length: SPARK_DAYS }, (_, i) => ({
        date: today.subtract(SPARK_DAYS - 1 - i, "day"),
        count: 0,
    }));

    rows.forEach((r) => {
        if (r.sentiment && counts[r.sentiment] !== undefined) counts[r.sentiment] += 1;
        if (r.reason) reasons.set(r.reason, (reasons.get(r.reason) || 0) + 1);
        if (r.feature_area) features.set(r.feature_area, (features.get(r.feature_area) || 0) + 1);
        if (r.status === "new") newCount += 1;
        if (r.status === "fixed") fixedCount += 1;
        if (r.status === "ignored") ignoredCount += 1;

        const isAuto = r.interaction_type === "auto" || /auto/i.test(r.interaction_type || "");
        if (isAuto) autoCount += 1;

        if (isActionableStatus(r.status)) {
            actionableCount += 1;
            if (r.sentiment && actionableCounts[r.sentiment] !== undefined) {
                actionableCounts[r.sentiment] += 1;
            }
            if (r.reason) actionableReasons.set(r.reason, (actionableReasons.get(r.reason) || 0) + 1);
            if (r.feature_area) actionableFeatures.set(r.feature_area, (actionableFeatures.get(r.feature_area) || 0) + 1);
            if (isAuto) actionableAutoCount += 1;
        }

        const d = dayjs(r.created_at).startOf("day");
        const idx = buckets.findIndex((b) => b.date.isSame(d, "day"));
        if (idx >= 0) buckets[idx].count += 1;
    });

    const top = (m, n = 3) => Array.from(m.entries()).sort((a, b) => b[1] - a[1]).slice(0, n);

    const pct = (n) => total ? Math.round((n / total) * 100) : 0;
    const actionablePct = (n) => actionableCount ? Math.round((n / actionableCount) * 100) : 0;

    return {
        total,
        sentiment: counts,
        sentimentPct: {
            positive: pct(counts.positive),
            neutral: pct(counts.neutral),
            negative: pct(counts.negative),
        },
        actionableSentiment: actionableCounts,
        actionableSentimentPct: {
            positive: actionablePct(actionableCounts.positive),
            neutral: actionablePct(actionableCounts.neutral),
            negative: actionablePct(actionableCounts.negative),
        },
        topReasons: top(reasons),
        topFeatures: top(features),
        topActionableReasons: top(actionableReasons),
        topActionableFeatures: top(actionableFeatures),
        newCount, autoCount, fixedCount, ignoredCount,
        actionableCount, actionableAutoCount,
        buckets,
    };
};

const ProductFeedbackDashboard = () => {
    const [items, setItems] = useState([]);
    const [statsRows, setStatsRows] = useState([]);
    const [statusTotals, setStatusTotals] = useState(() => emptyStatusTotals());
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(25);
    const [loading, setLoading] = useState(false);
    const [statsLoading, setStatsLoading] = useState(false);
    const [filters, setFilters] = useState({});
    const [selected, setSelected] = useState(null);
    const [updatingId, setUpdatingId] = useState(null);
    const [deletingId, setDeletingId] = useState(null);

    const loadFeedback = useCallback(async () => {
        setLoading(true);
        try {
            const response = await fetchProductFeedback({
                page, page_size: pageSize, ...filters,
            });
            const data = response?.data || {};
            setItems(data.items || []);
            setTotal(data.total || 0);
        } catch (error) {
            message.error("Impossible de charger les retours utilisateurs.");
        } finally {
            setLoading(false);
        }
    }, [filters, page, pageSize]);

    // Stats : second fetch indépendant. On reste sous 100 (limite probable backend).
    const loadStats = useCallback(async () => {
        setStatsLoading(true);
        try {
            const statusBreakdownFilters = { ...filters };
            delete statusBreakdownFilters.status;
            const [response, ...statusResponses] = await Promise.all([
                fetchProductFeedback({ page: 1, page_size: 100, ...filters }),
                ...STATUS_VALUES.map((status) => fetchProductFeedback({
                    page: 1,
                    page_size: 1,
                    ...statusBreakdownFilters,
                    status,
                })),
            ]);
            const data = response?.data || {};
            setStatsRows(data.items || []);
            const nextStatusTotals = emptyStatusTotals();
            STATUS_VALUES.forEach((status, index) => {
                nextStatusTotals[status] = statusResponses[index]?.data?.total || 0;
            });
            setStatusTotals(nextStatusTotals);
        } catch (error) {
            console.warn("[product-feedback] stats fetch failed:", error);
            setStatsRows([]);
            setStatusTotals(emptyStatusTotals());
        } finally {
            setStatsLoading(false);
        }
    }, [filters]);

    useEffect(() => { loadFeedback(); }, [loadFeedback]);
    useEffect(() => { loadStats(); }, [loadStats]);

    // Fallback : si le fetch stats n'a rien donné mais que la page courante en a,
    // on calcule au moins sur la page courante pour ne pas afficher 0 partout.
    const stats = useMemo(() => {
        const source = statsRows.length > 0 ? statsRows : items;
        return computeStats(source);
    }, [statsRows, items]);

    const setFilter = (key, value) => {
        setPage(1);
        setFilters((previous) => {
            const next = { ...previous };
            if (!value) delete next[key]; else next[key] = value;
            return next;
        });
    };

    const refreshAll = useCallback(() => {
        loadFeedback();
        loadStats();
    }, [loadFeedback, loadStats]);

    const changeStatus = useCallback(async (record, nextStatus) => {
        setUpdatingId(record.id);
        try {
            await updateProductFeedbackStatus(record.id, nextStatus);
            const shouldKeepVisible = !filters.status || filters.status === nextStatus;
            setItems((previous) => previous.map((item) => (
                item.id === record.id ? { ...item, status: nextStatus } : item
            )).filter((item) => item.id !== record.id || shouldKeepVisible));
            setStatsRows((previous) => previous.map((item) => (
                item.id === record.id ? { ...item, status: nextStatus } : item
            )));
            setSelected((previous) => (
                previous?.id === record.id
                    ? (shouldKeepVisible ? { ...previous, status: nextStatus } : null)
                    : previous
            ));
            setStatusTotals((previous) => {
                const next = { ...previous };
                const previousStatus = record.status || "new";
                if (next[previousStatus] !== undefined) {
                    next[previousStatus] = Math.max(0, next[previousStatus] - 1);
                }
                if (next[nextStatus] !== undefined) {
                    next[nextStatus] += 1;
                }
                return next;
            });
            if (!shouldKeepVisible) {
                setTotal((previous) => Math.max(0, previous - 1));
            }
            message.success("Statut mis à jour.");
        } catch (error) {
            message.error("Impossible de mettre à jour le statut.");
        } finally {
            setUpdatingId(null);
        }
    }, [filters.status]);

    const removeFeedbackLocally = useCallback((record) => {
        setItems((previous) => previous.filter((item) => item.id !== record.id));
        setStatsRows((previous) => previous.filter((item) => item.id !== record.id));
        setSelected((previous) => (previous?.id === record.id ? null : previous));
        setTotal((previous) => Math.max(0, previous - 1));
        setStatusTotals((previous) => {
            const next = { ...previous };
            const previousStatus = record.status || "new";
            if (next[previousStatus] !== undefined) {
                next[previousStatus] = Math.max(0, next[previousStatus] - 1);
            }
            return next;
        });
    }, []);

    const confirmDeleteFeedback = useCallback((record) => {
        if (!record?.id) return;
        Modal.confirm({
            title: "Supprimer ce feedback ?",
            content: "Action irreversible. Pour un vrai retour resolu, garde plutot le statut fixed; la suppression sert surtout au nettoyage des tests.",
            okText: "Supprimer",
            cancelText: "Annuler",
            okButtonProps: { danger: true },
            onOk: async () => {
                setDeletingId(record.id);
                try {
                    await deleteProductFeedback(record.id);
                    removeFeedbackLocally(record);
                    message.success("Feedback supprime.");
                    refreshAll();
                } catch (error) {
                    message.error("Impossible de supprimer ce feedback.");
                    throw error;
                } finally {
                    setDeletingId(null);
                }
            },
        });
    }, [refreshAll, removeFeedbackLocally]);

    const reviewLinkOf = (row) => {
        if (row?.analyse_id) return `/stage-review/${row.analyse_id}`;
        const m = String(row?.page_route || "").match(/\/stage-review\/[a-f0-9-]{36}/i);
        return m ? m[0] : null;
    };

    const selectedIndex = useMemo(() => {
        if (!selected?.id) return -1;
        return items.findIndex((item) => item.id === selected.id);
    }, [items, selected]);

    const goToFeedback = useCallback((direction) => {
        if (selectedIndex < 0) return;
        const nextIndex = selectedIndex + direction;
        if (nextIndex < 0 || nextIndex >= items.length) return;
        setSelected(items[nextIndex]);
    }, [items, selectedIndex]);

    useEffect(() => {
        if (!selected) return undefined;
        const handleKeyDown = (event) => {
            if (event.key === "Escape") {
                setSelected(null);
                return;
            }
            if (event.key === "ArrowUp" || event.key.toLowerCase() === "k") {
                event.preventDefault();
                goToFeedback(-1);
                return;
            }
            if (event.key === "ArrowDown" || event.key.toLowerCase() === "j") {
                event.preventDefault();
                goToFeedback(1);
            }
        };
        window.addEventListener("keydown", handleKeyDown);
        return () => window.removeEventListener("keydown", handleKeyDown);
    }, [goToFeedback, selected]);

    const columns = useMemo(() => [
        {
            title: "Date",
            dataIndex: "created_at",
            width: 120,
            render: (value) => (
                <Tooltip title={formatAbs(value)}>
                    <span className="cell-date">{formatRelative(value)}</span>
                </Tooltip>
            ),
        },
        {
            title: "User",
            dataIndex: "user_label",
            width: 180,
            render: (value) => (
                <div className="cell-user">
                    <Avatar size={24} className="cell-user__avatar">{initialsOf(value)}</Avatar>
                    <span className="cell-user__name">{value || "—"}</span>
                </div>
            ),
        },
        {
            title: "Feature",
            dataIndex: "feature_area",
            width: 156,
            render: (value, record) => (
                <Space size={6} align="center">
                    <Tag className="feedback-feature-tag">{FEATURE_LABEL[value] || value || "—"}</Tag>
                    {(record.interaction_type === "auto" || /auto/i.test(record.interaction_type || "")) && (
                        <Tooltip title="Auto-capture (erreur technique)">
                            <span className="cell-auto-pill"><Bot size={10} /></span>
                        </Tooltip>
                    )}
                </Space>
            ),
        },
        {
            title: "Sent.",
            dataIndex: "sentiment",
            width: 60,
            align: "center",
            render: (value) => <SentimentDot value={value} />,
        },
        {
            title: "Reason",
            dataIndex: "reason",
            width: 160,
            render: (value) => value ? <Tag className="cell-reason">{value}</Tag> : <span className="cell-muted">—</span>,
        },
        {
            title: "Message",
            dataIndex: "message",
            ellipsis: true,
            render: (value) => value
                ? <span className="cell-message">{short(value, 110)}</span>
                : <span className="cell-muted">—</span>,
        },
        {
            title: "Status",
            dataIndex: "status",
            width: 168,
            render: (value, record) => (
                <Select
                    size="small"
                    value={value}
                    options={STATUS_OPTIONS}
                    loading={updatingId === record.id}
                    onClick={(event) => event.stopPropagation()}
                    onChange={(next) => changeStatus(record, next)}
                    className={`feedback-status-select status-${value}`}
                    popupMatchSelectWidth={false}
                />
            ),
        },
        {
            title: "",
            key: "actions",
            width: 52,
            align: "center",
            render: (_, record) => (
                <Tooltip title="Supprimer ce feedback">
                    <Button
                        type="text"
                        danger
                        size="small"
                        icon={<Trash2 size={14} />}
                        loading={deletingId === record.id}
                        onClick={(event) => {
                            event.stopPropagation();
                            confirmDeleteFeedback(record);
                        }}
                        className="feedback-row-delete"
                        aria-label="Supprimer ce feedback"
                    />
                </Tooltip>
            ),
        },
    ], [changeStatus, confirmDeleteFeedback, deletingId, updatingId]);

    const statusBreakdownTotal = STATUS_VALUES.reduce((sum, status) => sum + (statusTotals[status] || 0), 0);
    const filteredPeriodTotal = filters.status ? total : Math.max(total, statusBreakdownTotal, stats.total);
    const actionableTotal = filters.status
        ? (isActionableStatus(filters.status) ? total : 0)
        : (statusBreakdownTotal > 0
            ? STATUS_VALUES.filter((status) => ACTIONABLE_STATUSES.has(status))
                .reduce((sum, status) => sum + (statusTotals[status] || 0), 0)
            : stats.actionableCount);
    const fixedTotal = filters.status ? (filters.status === "fixed" ? total : 0) : (statusTotals.fixed || stats.fixedCount);
    const ignoredTotal = filters.status ? (filters.status === "ignored" ? total : 0) : (statusTotals.ignored || stats.ignoredCount);
    const closedTotal = fixedTotal + ignoredTotal;
    const newTotal = filters.status ? (filters.status === "new" ? total : 0) : (statusTotals.new || stats.newCount);
    const negativePct = stats.actionableSentimentPct.negative;
    const newPct = actionableTotal ? Math.round((newTotal / actionableTotal) * 100) : 0;
    const autoPct = stats.actionableCount ? Math.round((stats.actionableAutoCount / stats.actionableCount) * 100) : 0;
    const topActionableFeature = stats.topActionableFeatures[0]
        ? (FEATURE_LABEL[stats.topActionableFeatures[0][0]] || stats.topActionableFeatures[0][0])
        : "—";

    return (
        <div className="product-feedback-page">
            <div className="product-feedback-toolbar">
                <div>
                    <Title level={3} className="product-feedback-title">Retours utilisateurs</Title>
                    <Text className="product-feedback-subtitle">
                        Signaux produit collectés depuis l'interface de revue.
                    </Text>
                </div>
                <Button
                    icon={<RefreshCw size={14} />}
                    onClick={refreshAll}
                    loading={loading || statsLoading}
                    className="product-feedback-refresh"
                >
                    Actualiser
                </Button>
            </div>

            {/* KPIs */}
            <div className="kpi-grid">
                <KpiCard
                    icon={Inbox}
                    label="A traiter"
                    value={actionableTotal.toLocaleString("fr-FR")}
                    hint={`${filteredPeriodTotal.toLocaleString("fr-FR")} total periode - ${closedTotal.toLocaleString("fr-FR")} fixed/ignored`}
                    accent="primary"
                >
                    <div className="kpi-card__spark">
                        <Sparkline buckets={stats.buckets} />
                    </div>
                </KpiCard>

                <KpiCard
                    icon={ThumbsDown}
                    label="Negatif ouvert"
                    value={`${negativePct}%`}
                    hint={`${stats.actionableSentiment.negative} negatifs ouverts - ${closedTotal.toLocaleString("fr-FR")} clos`}
                    accent={negativePct >= 40 ? "danger" : negativePct >= 20 ? "warning" : "default"}
                >
                    <div className="kpi-stack">
                        <div className="kpi-stack__bar">
                            <span style={{ width: `${stats.actionableSentimentPct.negative}%`, background: SENTIMENT_COLOR.negative }} />
                            <span style={{ width: `${stats.actionableSentimentPct.neutral}%`,  background: SENTIMENT_COLOR.neutral }} />
                            <span style={{ width: `${stats.actionableSentimentPct.positive}%`, background: SENTIMENT_COLOR.positive }} />
                        </div>
                    </div>
                </KpiCard>

                <KpiCard
                    icon={AlertTriangle}
                    label="Nouveaux"
                    value={newTotal.toLocaleString("fr-FR")}
                    hint={`${newPct}% des retours a traiter`}
                    accent={newPct >= 50 ? "warning" : "default"}
                >
                    <div className="kpi-card__list">
                        {stats.topActionableReasons.length === 0 && <span className="cell-muted">Aucun motif ouvert.</span>}
                        {stats.topActionableReasons.map(([reason, count]) => (
                            <div key={reason} className="kpi-row">
                                <span className="kpi-row__label">{reason}</span>
                                <span className="kpi-row__value">{count}</span>
                            </div>
                        ))}
                    </div>
                </KpiCard>

                <KpiCard
                    icon={Zap}
                    label="Top features ouvertes"
                    value={topActionableFeature}
                    hint={`${autoPct}% auto-capture ouverts`}
                >
                    <div className="kpi-card__list">
                        {stats.topActionableFeatures.length === 0 && <span className="cell-muted">Aucune feature ouverte.</span>}
                        {stats.topActionableFeatures.map(([feat, count]) => (
                            <div key={feat} className="kpi-row">
                                <span className="kpi-row__label">{FEATURE_LABEL[feat] || feat}</span>
                                <span className="kpi-row__value">{count}</span>
                            </div>
                        ))}
                    </div>
                </KpiCard>
            </div>

            {/* Filtres */}
            <div className="product-feedback-filters">
                <Select
                    allowClear placeholder="Feature"
                    options={FEATURE_OPTIONS.map((o) => ({ ...o, label: FEATURE_LABEL[o.value] || o.value }))}
                    value={filters.feature_area}
                    onChange={(value) => setFilter("feature_area", value)}
                />
                <Select
                    allowClear placeholder="Sentiment"
                    options={SENTIMENT_OPTIONS}
                    value={filters.sentiment}
                    onChange={(value) => setFilter("sentiment", value)}
                />
                <Select
                    allowClear placeholder="Reason"
                    options={REASON_OPTIONS}
                    value={filters.reason}
                    onChange={(value) => setFilter("reason", value)}
                />
                <Select
                    allowClear placeholder="Status"
                    options={STATUS_OPTIONS}
                    value={filters.status}
                    onChange={(value) => setFilter("status", value)}
                />
            </div>

            {/* Table */}
            <Table
                className="product-feedback-table"
                rowKey="id"
                columns={columns}
                dataSource={items}
                loading={loading}
                size="middle"
                locale={{ emptyText: <Empty description="Aucun retour pour ces filtres." /> }}
                onRow={(record) => ({
                    onClick: () => setSelected(record),
                    className: selected?.id === record.id ? "feedback-row-active" : "",
                })}
                pagination={{
                    current: page,
                    pageSize,
                    total,
                    showSizeChanger: true,
                    pageSizeOptions: [10, 25, 50, 100],
                    showTotal: (n) => `${n} retours`,
                    onChange: (nextPage, nextPageSize) => {
                        setPage(nextPage);
                        setPageSize(nextPageSize);
                    },
                }}
            />

            {/* Drawer détail · Linear-grade (sticky header + nav clavier ↑↓/JK) */}
            <Drawer
                open={!!selected}
                onClose={() => setSelected(null)}
                width="min(92vw, 760px)"
                title={null}
                closable={false}
                className="feedback-detail-drawer"
                destroyOnClose
            >
                {selected && (() => {
                    const reviewLink = reviewLinkOf(selected);
                    const hasPrev = selectedIndex > 0;
                    const hasNext = selectedIndex >= 0 && selectedIndex < items.length - 1;
                    return (
                        <div className="feedback-detail">
                            {/* ───── Sticky header : navigation + identité ───── */}
                            <div className="feedback-detail__sticky">
                                <div className="feedback-detail__nav">
                                    <span className="feedback-detail__nav-counter">
                                        {selectedIndex >= 0 ? (
                                            <>
                                                <strong>{selectedIndex + 1}</strong>
                                                <span className="feedback-detail__nav-sep">/</span>
                                                <span>{items.length}</span>
                                            </>
                                        ) : "—"}
                                    </span>
                                    <Tooltip title="Précédent (↑ ou K)">
                                        <Button
                                            type="text"
                                            size="small"
                                            icon={<ChevronUp size={15} />}
                                            disabled={!hasPrev}
                                            onClick={() => goToFeedback(-1)}
                                            className="feedback-detail__nav-btn"
                                            aria-label="Feedback précédent"
                                        />
                                    </Tooltip>
                                    <Tooltip title="Suivant (↓ ou J)">
                                        <Button
                                            type="text"
                                            size="small"
                                            icon={<ChevronDown size={15} />}
                                            disabled={!hasNext}
                                            onClick={() => goToFeedback(1)}
                                            className="feedback-detail__nav-btn"
                                            aria-label="Feedback suivant"
                                        />
                                    </Tooltip>
                                    <span className="feedback-detail__nav-spacer" />
                                    <span className="feedback-detail__kbd-hint">
                                        <kbd>↑</kbd><kbd>↓</kbd> naviguer · <kbd>Esc</kbd> fermer
                                    </span>
                                    <Tooltip title="Supprimer ce feedback">
                                        <Button
                                            type="text"
                                            danger
                                            size="small"
                                            icon={<Trash2 size={15} />}
                                            loading={deletingId === selected.id}
                                            onClick={() => confirmDeleteFeedback(selected)}
                                            className="feedback-detail__nav-btn feedback-detail__delete-btn"
                                            aria-label="Supprimer ce feedback"
                                        />
                                    </Tooltip>
                                    <Tooltip title="Fermer (Esc)">
                                        <Button
                                            type="text"
                                            size="small"
                                            icon={<X size={15} />}
                                            onClick={() => setSelected(null)}
                                            className="feedback-detail__nav-btn"
                                            aria-label="Fermer le drawer"
                                        />
                                    </Tooltip>
                                </div>

                                <div className="feedback-detail__header">
                                    <span className="feedback-detail__eyebrow">
                                        Feedback · {formatAbs(selected.created_at)}
                                    </span>
                                    <Title level={4} className="feedback-detail__title">
                                        {FEATURE_LABEL[selected.feature_area] || selected.feature_area}
                                    </Title>
                                    <Space size={6} wrap>
                                        <Tag color={STATUS_COLOR[selected.status]}>{selected.status}</Tag>
                                        {selected.sentiment && (
                                            <Tag color={selected.sentiment === "negative" ? "red"
                                                : selected.sentiment === "positive" ? "green" : "default"}>
                                                {selected.sentiment}
                                            </Tag>
                                        )}
                                        {selected.reason && <Tag>{selected.reason}</Tag>}
                                        {(selected.interaction_type === "auto" || /auto/i.test(selected.interaction_type || "")) && (
                                            <Tag color="purple"><Bot size={10} style={{ marginRight: 4 }} />auto-capture</Tag>
                                        )}
                                    </Space>
                                </div>
                            </div>

                            <section className="feedback-detail__section feedback-detail__section--full">
                                <div className="feedback-detail__section-head">
                                    <div className="feedback-detail__label">Message</div>
                                    {reviewLink && (
                                        <a href={reviewLink} target="_blank" rel="noreferrer" className="feedback-detail__link">
                                            Ouvrir la revue <ExternalLink size={11} />
                                        </a>
                                    )}
                                </div>
                                <Paragraph className="feedback-detail__message">
                                    {selected.message || "Aucun message libre."}
                                </Paragraph>
                            </section>

                            <div className="feedback-detail__grid">
                                <section className="feedback-detail__section">
                                    <div className="feedback-detail__label">Contexte principal</div>
                                    <div className="feedback-detail-idline">
                                        <span>Page</span><code>{selected.page_route || "—"}</code>
                                    </div>
                                    <div className="feedback-detail-idline">
                                        <span>User</span>
                                        <code>
                                            <Avatar size={18} style={{ marginRight: 6, verticalAlign: "middle" }}>
                                                {initialsOf(selected.user_label)}
                                            </Avatar>
                                            {selected.user_label || "—"}
                                        </code>
                                    </div>
                                    <div className="feedback-detail-idline">
                                        <span>Target</span>
                                        <code>{[selected.target_type, selected.target_id].filter(Boolean).join(" / ") || "—"}</code>
                                    </div>
                                    {selected.context_json?.comment_title && (
                                        <div className="feedback-detail-idline">
                                            <span>Titre commentaire</span><code>{selected.context_json.comment_title}</code>
                                        </div>
                                    )}
                                    {selected.context_json?.criterion_title && (
                                        <div className="feedback-detail-idline">
                                            <span>Critère</span><code>{selected.context_json.criterion_title}</code>
                                        </div>
                                    )}
                                    {selected.context_json?.error_kind && (
                                        <div className="feedback-detail-idline">
                                            <span>Erreur</span>
                                            <code>{selected.context_json.error_kind}{selected.context_json.error_message ? ` · ${selected.context_json.error_message}` : ""}</code>
                                        </div>
                                    )}
                                </section>

                                <section className="feedback-detail__section">
                                    <div className="feedback-detail__label">IDs liés</div>
                                    <div className="feedback-detail-idline"><span>analyse_id</span><code>{selected.analyse_id || "—"}</code></div>
                                    <div className="feedback-detail-idline"><span>document_version_id</span><code>{selected.document_version_id || "—"}</code></div>
                                    <div className="feedback-detail-idline"><span>ai_comment_id</span><code>{selected.ai_comment_id || "—"}</code></div>

                                    <div className="feedback-detail__label" style={{ marginTop: 14 }}>Statut</div>
                                    <Select
                                        value={selected.status}
                                        options={STATUS_OPTIONS}
                                        onChange={(next) => changeStatus(selected, next)}
                                        loading={updatingId === selected.id}
                                        className="feedback-detail__status"
                                    />
                                </section>
                            </div>

                            <FeedbackExplainPanel feedbackId={selected.id} />

                            <section className="feedback-detail__section feedback-detail__section--full">
                                <div className="feedback-detail__label">context_json</div>
                                <pre className="feedback-detail__json">
                                    {JSON.stringify(selected.context_json || {}, null, 2)}
                                </pre>
                            </section>
                        </div>
                    );
                })()}
            </Drawer>
        </div>
    );
};

export default ProductFeedbackDashboard;
